import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import LikeButton from '@components/LikeButton';
import ShareButton from '@components/ShareButton';
import { Alert, FlexView, Loading, Text } from '@goodfynd/ui';
import TextField from '@mui/material/TextField';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';

import config from '../../config';
import images from '../../config/images';
import strings from '../../config/strings';
import { useApp } from '../../context/app-context';
import { useCart, useCartDispatch } from '../../context/cart-context';
import { useProductApi } from '../../hooks/api';
import { useOrderActions } from '../../hooks/useOrderActions';
import type { ProductResponse } from '../../services/api/types';
import { useStores } from '../../stores';
import * as numberUtil from '../../utils/number-util';
import * as storageUtil from '../../utils/storage-util';
import InputQuantity from '../forms/InputQuantity/InputQuantity';
import Modal from '../Modal';
import PickupTimeOptions from '../PickupTimeOptions';

import ProductOptionsGroup from './ProductOptionsGroup';
import {
  StyledAddButton,
  StyledImageContainer,
  StyledModalContent,
  StyledRequestsContainer,
  StyledStickyBottom,
} from './styles';
import type { ProductModalProps } from './types';

import classes from './ProductModal.module.css';

const ProductModal = (props: ProductModalProps) => {
  const { isGoodfyndSite, orderingEnabled } = useCart();
  const { setCart } = useCartDispatch();

  const [errors, setErrors] = useState<string[]>([]);
  const [requests, setRequests] = useState('');
  const [viewPickupTimeOptions, togglePickupTimeOptions] = useState(false);

  const refs = useRef<Record<string, React.RefObject<HTMLDivElement>>>({});
  const [invalidGroup, setInvalidGroup] = useState('');

  const {
    user: { isGuest },
  } = useApp();

  const {
    orderStore: { busy, event, request, startOrder },
  } = useStores();

  const { addItem } = useOrderActions();

  const [quantity, setQuantity] = useState(1);
  const {
    pathname,
    query: { params, id: scheduleId },
  } = useRouter();
  const id = params?.[params.length - 1] as string;

  /* Fetch product data */
  const api = useProductApi();
  const { data, isLoading } = useQuery<ProductResponse, unknown>(
    id,
    () => {
      return api.get(id);
    },
    {
      refetchOnMount: true,
    }
  );

  useEffect(() => {
    if (!event?.pickupTime && orderingEnabled) {
      togglePickupTimeOptions(true);
    }
  }, [event?.pickupTime, orderingEnabled]);

  useEffect(() => {
    if (data && data?.product?.id === id && event?.pickupTime) {
      const product = data.product;
      startOrder(id, {
        id: '',
        isDonation: false,
        optionGroups: product.optionGroups.map((group) => ({
          ...group,
          limit: group.limit || 0,
          options: [],
        })),
        orderId: '',
        pickupTime: undefined,
        productId: product.id,
        quantity: quantity,
        requests,
        scheduleId: '',
        sector: 'trucks',
        value: product.price.value || 0, // price
      });
    }
  }, [data, event?.pickupTime, id, quantity, requests, startOrder]);

  if (!isLoading && !data?.product) {
    return null;
  }

  const isValid = () => {
    if (!request?.optionGroups.length) {
      return true;
    }

    for (let i = 0; i < request.optionGroups.length; i++) {
      const group = request.optionGroups[i];
      const valid = !group.required || group.options.length > 0;
      if (valid) {
        continue;
      }

      const groupId = group.id;
      const ref = refs.current[groupId];
      ref.current?.scrollIntoView({ behavior: 'smooth' });
      setInvalidGroup(groupId);
      return false;
    }

    return true;
  };

  const handleAdd = async () => {
    if (data?.product && isValid()) {
      try {
        setErrors([]);
        const cart = await addItem(quantity, requests, data.product.name);
        setCart(cart);

        if (isGuest) {
          storageUtil.local.set(config.storageKeys.guestOrder, cart.list[0].id);
        } else {
          storageUtil.local.remove(config.storageKeys.guestOrder);
        }

        props.close();
      } catch (error: unknown) {
        console.warn(error);

        const errorMessages: string[] = [];
        if (error && typeof error === 'object') {
          const err = error as Record<string, unknown>;
          if (typeof err.message === 'string') {
            errorMessages.push(err.message);
          }
          if (Array.isArray(err.errors)) {
            err.errors
              .filter(
                (e: unknown) =>
                  typeof e === 'object' &&
                  e !== null &&
                  (e as Record<string, unknown>).code !== 'trace'
              )
              .forEach((e: unknown) => {
                const errorObj = e as Record<string, unknown>;
                if (typeof errorObj.message === 'string') {
                  errorMessages.push(errorObj.message);
                }
              });
          }
        }
        setErrors(errorMessages);
      }
    }
  };

  const renderContent = () => {
    if (busy || isLoading) {
      return (
        <div>
          <Loading type="line" />
        </div>
      );
    }

    if (!data?.product) {
      return <></>;
    }

    const product = data.product;
    return (
      <div>
        <StyledImageContainer
          css={{
            backgroundImage: `url(${
              product.image || images.fallbacks.product
            })`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
          }}
        >
          <FlexView className={classes['image-button-container']} gap={16}>
            {!isGoodfyndSite && (
              <LikeButton
                buttonStyle="icon"
                className="image-button-shadow"
                count={product.likes}
                item={product}
                liked={product.liked}
                rank="tertiary"
                type="product"
              />
            )}

            <ShareButton
              buttonStyle="icon"
              className="image-button-shadow"
              rank="tertiary"
              title={`${product.name} @ ${product.vendor.uniqueName}`}
              url={window.location.href}
              text={`Check out ${product.name} from ${
                product.vendor.uniqueName
              }${isGoodfyndSite ? '' : ' on Goodfynd'}!`}
            />
          </FlexView>
        </StyledImageContainer>

        <StyledModalContent
          css={{
            paddingBottom: isDisabled ? 24 : undefined,
          }}
        >
          <Text as="heading5">{product.name}</Text>
          <Text as="body2" style={{ marginTop: 8 }}>
            {product.description}
          </Text>

          {isDisabled && orderingEnabled && (
            <Alert className={classes['disabled-alert']} status="warning">
              {event?.pickupTime ? (
                strings.labels.itemNotAvailable
              ) : (
                <Text
                  color="TextIconFavorable"
                  onClick={() => togglePickupTimeOptions(true)}
                  style={{ cursor: 'pointer' }}
                >
                  {strings.titles.choosePickupOptions}
                </Text>
              )}
            </Alert>
          )}

          {errors.length > 0 && (
            <Alert status="error" style={{ marginTop: 12 }}>
              {errors}
            </Alert>
          )}

          <ProductOptionsGroup
            groups={product.optionGroups}
            invalidGroup={invalidGroup}
            refs={refs}
          />

          {product.allowSpecialRequests && orderingEnabled && (
            <StyledRequestsContainer>
              <TextField
                disabled={isDisabled}
                fullWidth
                helperText={strings.labels.leaveNotes}
                label={strings.labels.specialRequests}
                onChange={(e) => setRequests(e.target.value)}
                value={requests}
              />
            </StyledRequestsContainer>
          )}
        </StyledModalContent>
      </div>
    );
  };

  const product = data?.product;
  const isDisabled =
    !event?.pickupTime || !product?.price.value || !orderingEnabled;
  return (
    <>
      <Modal
        aria-describedby="product-modal-ordering"
        aria-labelledby="product-modal"
        closeButtonClassName="image-button-shadow"
        footer={
          <StyledStickyBottom>
            {!isDisabled && (
              <InputQuantity
                defaultValue={quantity}
                disabled={isDisabled}
                max={5}
                min={1}
                onChange={(quantity) => setQuantity(quantity)}
              />
            )}

            <StyledAddButton disabled={isDisabled} onClick={handleAdd}>
              {product?.price.value
                ? strings.labels.add +
                  config.unicode.centerDotPadded +
                  numberUtil.renderPrice(
                    (product?.price.value +
                      (request?.optionGroups
                        .map((group) =>
                          group.options
                            .map((option) => option.price || 0)
                            .reduce((a, b) => a + b, 0)
                        )
                        .reduce((a, b) => a + b, 0) || 0)) *
                      quantity
                  )
                : strings.labels.notForSale}
            </StyledAddButton>
          </StyledStickyBottom>
        }
        {...props}
      >
        {renderContent()}
      </Modal>

      {product && viewPickupTimeOptions && (
        <PickupTimeOptions
          onContinue={() => togglePickupTimeOptions(false)}
          open={viewPickupTimeOptions}
          vendorId={product.vendor.nameId}
          close={() => {
            togglePickupTimeOptions(false);
          }}
          scheduleId={
            //TODO find a better way to  get the schedule id.
            pathname.includes('schedules') ? (scheduleId as string) : undefined
          }
        />
      )}
    </>
  );
};

export default observer(ProductModal);
